import React from 'react';
import { TextField, MenuItem, Checkbox, Select, Stack, Box } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import type { InlineEditorProps } from './CellValue';
import moment, { Moment } from 'moment';
import ClientsAutoComplete from 'components/common/ClientsAutocomplete';
import CandidateAutocomplete from 'components/candidate/CandidateAutocomplete';
import type { Candidate } from 'components/candidate/types';
import PhonesManager from 'components/common/PhonesManager';
import type { PhoneItem } from 'components/common/PhonesManager';
import EmailsManager from 'components/common/EmailsManager';
import type { EmailItem } from 'components/common/EmailsManager';
import AddressManager from 'components/common/AddressManager';
import { IAddress } from 'components/types';
import SocialMediaLinksManager from 'components/common/SocialMediaLinksManager';
import Uploader from 'components/common/Uploader';
import type { IAttachment } from 'components/types';
import LogoOrFromName from 'components/common/LogoOrFromName';

export const inlineFieldSx = (hasError: boolean) => ({
  '& .MuiInputBase-root': {
    border: hasError ? '1px solid' : undefined,
    borderColor: hasError ? 'error.main' : undefined,
    borderRadius: 1,
    px: 0.5,
  },
});

export const InlineTextEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<string>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <TextField
      variant="outlined"
      fullWidth
      inputRef={inputRef}
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          e.preventDefault();
          onCommit(value);
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          onCancel();
        }
      }}
      disabled={disabled}
      error={hasError}
      inputProps={{ sx: { py: 0 } }}
      sx={inlineFieldSx(hasError)}
    />
  );
};

export const InlineTextareaEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<string>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const safeValue = value ?? '';
    const len = safeValue.length;
    el.setSelectionRange(len, len);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TextField
      multiline
      rows={3}
      variant="outlined"
      fullWidth
      inputRef={inputRef}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          if (e.ctrlKey || e.shiftKey) {
            e.stopPropagation();
            e.preventDefault();
            onCommit(value ?? '');
          }
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          onCancel();
        }
      }}
      disabled={disabled}
      error={hasError}
      inputProps={{ sx: { py: 0.5 } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          bgcolor: 'background.paper',
          fontSize: '0.875rem',
          py: 0.5,
          ...(hasError && { borderColor: 'error.main' }),
        },
      }}
    />
  );
};

export const InlineNumberEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<number | null>) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <TextField
      type="number"
      variant="outlined"
      fullWidth
      inputRef={inputRef}
      value={value === null ? '' : value}
      onChange={e => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(null);
          return;
        }
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) return;
        onChange(parsed);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          e.preventDefault();
          onCommit(value);
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          onCancel();
        }
      }}
      disabled={disabled}
      error={hasError}
      inputProps={{ sx: { py: 0 } }}
      sx={inlineFieldSx(hasError)}
    />
  );
};

export const InlineSelectEditor = <V extends string>({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
  options = [],
}: InlineEditorProps<V>) => {
  const theme = useTheme();
  const committingRef = React.useRef(false);

  return (
    <Select
      value={value}
      open
      onClose={() => {
        if (!committingRef.current) {
          onCancel();
        }
      }}
      onChange={e => {
        const v = e.target.value as V;
        onChange(v);
        committingRef.current = true;
        Promise.resolve(onCommit(v)).finally(() => {
          committingRef.current = false;
        });
      }}
      size="small"
      fullWidth
      disabled={disabled}
      error={hasError}
      autoFocus
      MenuProps={{
        sx: { zIndex: theme.zIndex.inlineEditorMenu },
      }}
    >
      {options.map(opt => (
        <MenuItem key={String(opt.value)} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};

export const InlineDateEditor = ({
  value,
  onChange,
  onCommit,
  disabled,
}: InlineEditorProps<{ date: Date | null; iso: string }>) => {
  const momentValue = value.date ? moment(value.date) : null;

  return (
    <DateCalendar
      value={momentValue}
      onChange={(newMoment: Moment | null) => {
        if (disabled) return;
        const next = {
          date: newMoment ? newMoment.toDate() : null,
          iso: newMoment ? newMoment.toISOString() : '',
        };
        onChange(next);
        onCommit(next);
      }}
      disabled={disabled}
    />
  );
};

export const InlineBooleanEditor = ({ value, onChange, onCommit, disabled, hasError }: InlineEditorProps<boolean>) => {
  const committingRef = React.useRef(false);

  const handleToggle = () => {
    if (disabled || committingRef.current) return;
    const newValue = !value;
    committingRef.current = true;
    onChange(newValue);
    Promise.resolve(onCommit(newValue)).finally(() => {
      committingRef.current = false;
    });
  };

  return (
    <Checkbox
      checked={value}
      onChange={handleToggle}
      disabled={disabled}
      sx={{
        p: 0,
        color: hasError ? 'error.main' : undefined,
        '&.Mui-checked': {
          color: hasError ? 'error.main' : 'secondary.main',
        },
      }}
    />
  );
};

export type { EmailItem } from 'components/common/EmailsManager';

export const InlineEmailsEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
  onSaveField,
  meta,
}: InlineEditorProps<EmailItem[]>) => {
  const theme = useTheme();
  return (
    <EmailsManager
      inlineConfig={{
        value,
        onChange,
        onCommit,
        onCancel,
        disabled,
        hasError,
        autoFocus: true,
        selectZIndex: theme.zIndex.inlineEditorMenu,
        onSaveField,
        initialPrimaryEmail: meta?.primaryEmail as string | undefined,
      }}
    />
  );
};

export const InlinePhonesEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
  onSaveField,
  meta,
}: InlineEditorProps<PhoneItem[]>) => {
  const theme = useTheme();
  return (
    <PhonesManager
      inlineConfig={{
        value,
        onChange,
        onCommit,
        onCancel,
        disabled,
        hasError,
        autoFocus: true,
        selectZIndex: theme.zIndex.inlineEditorMenu,
        onSaveField,
        initialPrimaryPhone: meta?.primaryPhone as string | undefined,
      }}
    />
  );
};

export type ClientValue = { clientId: string; clientName: string };

export const InlineClientEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
}: InlineEditorProps<ClientValue>) => {
  const defaultValue = value.clientId ? { _id: value.clientId, name: value.clientName } : undefined;

  const handleSelect = (client: { _id?: string; name: string } | null) => {
    const next = { clientId: client?._id || '', clientName: client?.name || '' };
    onChange(next);
    onCommit(next);
  };

  return (
    <ClientsAutoComplete
      onSelect={handleSelect}
      disabled={disabled}
      defaultValue={defaultValue}
      autocompleteSlotProps={{ popper: { sx: { zIndex: (t: Theme) => t.zIndex.inlineEditorMenu } } }}
    />
  );
};

export type CandidateValue = { candidateId: string; candidateName: string };

export const InlineCandidateEditor = ({ value, onChange, onCommit, disabled }: InlineEditorProps<CandidateValue>) => {
  const nameParts = (value.candidateName || '').trim().split(/\s+/);
  const defaultValue = value.candidateId
    ? ({
        _id: value.candidateId,
        firstName: nameParts[0] || value.candidateName || '',
        lastName: nameParts.slice(1).join(' ') || '',
      } as Candidate)
    : undefined;

  const handleSelect = (candidate: Candidate | null) => {
    const candidateName = candidate ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() : '';
    const next = { candidateId: candidate?._id || '', candidateName };
    onChange(next);
    void onCommit(next);
  };

  return (
    <CandidateAutocomplete
      onSelect={handleSelect}
      disabled={disabled}
      defaultValue={defaultValue}
      sx={{ width: '100%', minWidth: 280 }}
      textFieldStyle={{ width: '100%' }}
      slotProps={{ popper: { sx: { zIndex: (t: Theme) => t.zIndex.inlineEditorMenu } } }}
    />
  );
};

export const InlineNameEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<{ firstName: string; lastName: string }>) => {
  const firstNameRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  return (
    <Stack spacing={1.25} sx={{ minWidth: 280 }}>
      <TextField
        fullWidth
        variant="outlined"
        inputRef={firstNameRef}
        value={value.firstName}
        onChange={e => onChange({ ...value, firstName: e.target.value })}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            onCommit(value);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
            onCancel();
          }
        }}
        disabled={disabled}
        error={hasError}
        placeholder="First name"
        sx={inlineFieldSx(hasError)}
      />
      <TextField
        fullWidth
        variant="outlined"
        value={value.lastName}
        onChange={e => onChange({ ...value, lastName: e.target.value })}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            onCommit(value);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
            onCancel();
          }
        }}
        disabled={disabled}
        error={hasError}
        placeholder="Last name"
        sx={inlineFieldSx(hasError)}
      />
    </Stack>
  );
};

export const InlineAddressEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<IAddress>) => (
  <AddressManager
    inlineConfig={{
      value,
      onChange,
      onCommit,
      onCancel,
      disabled,
      hasError,
      autoFocus: true,
    }}
  />
);

export const InlineLinksEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<string[]>) => (
  <SocialMediaLinksManager
    inlineConfig={{
      value,
      onChange,
      onCommit,
      onCancel,
      disabled,
      hasError,
      autoFocus: true,
    }}
  />
);

export const InlineImageEditor = ({
  value,
  onChange,
  onCommit,
  disabled,
  autoFocus,
  meta,
}: InlineEditorProps<string>) => {
  const [uploading, setUploading] = React.useState(false);
  const hiddenInputRef = React.useRef<HTMLInputElement>(null);
  const fallbackName = (meta?.name as string) ?? '';

  React.useEffect(() => {
    if (autoFocus) {
      hiddenInputRef.current?.focus();
    }
  }, [autoFocus]);

  return (
    <Stack direction="column" spacing={1.5} alignItems="center" sx={{ minWidth: 240 }}>
      {/* Hidden input receives focus so Escape requires two presses (first blurs, second cancels) */}
      <input
        ref={hiddenInputRef}
        tabIndex={-1}
        aria-hidden="true"
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
      />
      <Box sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LogoOrFromName object={{ logo: value, name: fallbackName }} style={{ maxWidth: 80, maxHeight: 80 }} />
      </Box>
      <Box
        sx={{
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Uploader
          label={uploading ? 'Uploading…' : 'Upload'}
          multiple={false}
          showList={false}
          accept=".png,.jpg,.jpeg,.svg,.webp,.gif"
          onStartUploading={() => setUploading(true)}
          onFinishUploading={() => setUploading(false)}
          onChange={(result: IAttachment | IAttachment[]) => {
            if (Array.isArray(result)) return;
            if (!result?.url) return;
            onChange(result.url);
            onCommit(result.url);
          }}
        />
      </Box>
    </Stack>
  );
};
